import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { useResetPassword } from '../hooks/useResetPassword';
import {
  resetPasswordSchema,
  type ResetPasswordPayload,
  type UserIdAndName,
} from '../types/user.types';

interface ResetPasswordModalProps {
  user: UserIdAndName | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ResetPasswordModal({ user, open, onOpenChange }: ResetPasswordModalProps) {
  const [show, setShow] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ResetPasswordPayload>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '' },
  });

  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty);

  const { mutate, isPending } = useResetPassword(() => {
    onOpenChange(false);
    reset();
  });

  function handleReset(values: ResetPasswordPayload) {
    if (!user) return;
    mutate({ id: String(user.id), payload: values });
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) onOpenChange(true);
    else askBeforeClose(() => onOpenChange(false));
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {user?.fullName}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleReset)} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-password">New Password</Label>
              <div className="relative">
                <Input
                  id="reset-password"
                  type={show ? 'text' : 'password'}
                  placeholder="Enter new password"
                  className="h-10 pr-10"
                  {...register('newPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShow((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-xs text-destructive">{errors.newPassword.message}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {discardChangesDialog}
    </>
  );
}
